import './AmcGeo.scss';
import { endOfMonth, format, parseISO, startOfMonth, subMonths } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { abbreviateNumber } from '@src/utils/data';
import { useSetError } from '@src/redux/modals';
import { BPMTable, Header } from '@Components/StickyTable/BPMTable';
import { CellRenderer } from '@Components/StickyTable/StickyTable';
import { ChartContainer } from '@Components/ChartContainer';
import { Design, Placement, Size } from '@Components/designConstants';
import { DatePicker } from '@Components/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Dropdown } from '@Components/Dropdown';
import { StateMap } from '@Components/StateMap';
import { AmcGeoDataCards } from './AmcGeoDataCards';
import { getGeoTableHeaders } from './amc-geo-config';
import { AmcGeoTableRow } from './amc-geo-types';
import { useAmcGeoData } from './utils/useAmcGeoData';

// Fields the totals row displays as a percentage rather than an abbreviated count.
const PERCENT_FIELDS = new Set(['dpvRate', 'conversionRate', 'ntbRepeatRate', 'ntbPurchaseRate']);

const formatTotalsCell = (data: unknown, fieldName?: string): string | number => {
  if (fieldName === 'state') return String(data);
  if (fieldName && PERCENT_FIELDS.has(fieldName)) return `${data}%`;
  return abbreviateNumber(Number(data));
};

const buildTotalsRenderer = (
  headers: Header[]
): CellRenderer<Element | number | string | undefined> =>
  function TotalsCell({ data, style, classes, columnIndex }) {
    return (
      <div className={classes.join(' ')} style={style}>
        {formatTotalsCell(data, headers[columnIndex]?.name)}
      </div>
    );
  };

const DATA_POINT_OPTIONS = [
  { label: 'Impressions', value: 'impressions' },
  { label: 'Reach', value: 'reach' },
  { label: 'DPV', value: 'dpv' },
  { label: 'Customers Converted', value: 'customersConverted' },
  { label: 'Orders', value: 'orders' },
  { label: 'Revenue', value: 'revenue' },
  { label: 'NTB Customers', value: 'ntbCustomers' },
  { label: 'DPV Rate', value: 'dpvRate' },
  { label: 'Conversion Rate', value: 'conversionRate' },
  { label: 'NTB Repeat Rate', value: 'ntbRepeatRate' }
];

const GRAND_TOTAL_KEY = 'grandTotal';
const DATE_FORMAT = 'yyyy-MM-dd';

// Previous month's report isn't prepared until the 15th of the current month.
const monthsBack = new Date().getDate() < 15 ? 2 : 1;

// DatePicker's fullMonthsOnly mode represents both bounds as month-START strings (its job is
// "which months," not "which exact days") — `end` here is the start of the END month, not its
// last day. Converted to a real end-of-month value where the API call needs one, below.
const DEFAULT_DATE_RANGE: DateRange = {
  start: format(startOfMonth(subMonths(new Date(), monthsBack + 2)), DATE_FORMAT),
  end: format(startOfMonth(subMonths(new Date(), monthsBack)), DATE_FORMAT)
};

const stateMapTooltipFormatter = (dataPoint: string, dataPoints: Record<string, unknown>, d) => {
  const label = dataPoint
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/ntb/i, 'NTB')
    .replace(/dpv/i, 'DPV');
  return `${label}: ${abbreviateNumber(Number(dataPoints[d.properties.name]))}`;
};

const NoGeoData = () => <div className='amc-geo-no-data'>No data to show</div>;

interface AmcGeoProps {
  amcInstanceId?: string;
}

export const AmcGeo = ({ amcInstanceId }: AmcGeoProps) => {
  const [stateSelection, setStateSelection] = useState(GRAND_TOTAL_KEY);
  const [dataPointSelection, setDataPointSelection] = useState('impressions');
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const setError = useSetError();

  const {
    data: geoData,
    isLoading,
    isFetching,
    error
  } = useAmcGeoData(
    amcInstanceId,
    dateRange.start,
    format(endOfMonth(parseISO(dateRange.end)), DATE_FORMAT),
    stateSelection === GRAND_TOTAL_KEY ? null : stateSelection
  );

  useEffect(() => {
    if (error) {
      setError({
        title: 'Error fetching Geo data',
        message: 'There was an error fetching the Geo data. Please try again later.'
      });
    }
  }, [error, setError]);

  const maxValues = useMemo(
    () => ({
      dpvRate: Math.max(...(geoData?.data.map(row => row.dpvRate || 0) ?? [0])),
      conversionRate: Math.max(...(geoData?.data.map(row => row.conversionRate || 0) ?? [0])),
      ntbRepeatRate: Math.max(...(geoData?.data.map(row => row.ntbRepeatRate || 0) ?? [0])),
      purchaseRate: Math.max(...(geoData?.data.map(row => row.ntbPurchaseRate || 0) ?? [0]))
    }),
    [geoData]
  );

  const tableRows = geoData?.data ?? [];
  const headers = getGeoTableHeaders(maxValues);

  const dataCardData: Partial<AmcGeoTableRow> =
    (stateSelection === GRAND_TOTAL_KEY
      ? geoData?.total
      : tableRows.find(row => row.state === stateSelection)) ?? {};

  const isBusy = isFetching || isLoading;

  return (
    <div className='amc-geo'>
      <div className='amc-geo-header'>
        <DatePicker
          range={dateRange}
          onChange={setDateRange}
          fullMonthsOnly
          fiscalCalOverride={{}}
          design={Design.NEUTRAL}
        />
      </div>

      <ChartContainer
        leftActions={
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            className='amc-geo-data-point-dropdown'
            onChange={setDataPointSelection}
            options={DATA_POINT_OPTIONS}
            value={dataPointSelection}
          />
        }
        isLoading={isBusy}
        minHeight={650}
      >
        <div className='amc-geo-map-section'>
          <StateMap
            data={tableRows}
            dataPoint={dataPointSelection}
            resetValue={GRAND_TOTAL_KEY}
            tooltipFormatter={stateMapTooltipFormatter}
            updateSelection={setStateSelection}
          />
          <AmcGeoDataCards
            geoCardData={dataCardData}
            title={stateSelection === GRAND_TOTAL_KEY ? 'Grand Total' : stateSelection}
          />
        </div>
      </ChartContainer>

      <ChartContainer isLoading={isBusy} minHeight={650}>
        <div className='amc-geo-table-wrapper'>
          <BPMTable
            data={tableRows}
            filterBar={false}
            headers={headers}
            noRowsRenderer={NoGeoData}
            pagination
            totals={geoData?.total}
            totalsRenderer={buildTotalsRenderer(headers)}
          />
        </div>
      </ChartContainer>
    </div>
  );
};
